import { useState } from "react";
import { requestCredits } from "../api";
import type { AxiosError } from "axios";
import axios from "axios";

const useRequestCredits = (): {
  isRequestingCredits: boolean;
  mantineAlert: { message: string; color: string } | null;
  handleRequestCredits: () => Promise<void>;
  hasPendingRequest: boolean;
} => {
  const [isRequestingCredits, setIsRequestingCredits] = useState(false);
  const [hasPendingRequest, setHasPendingRequest] = useState(false);
  const [mantineAlert, setMantineAlert] = useState<{
    message: string;
    color: string;
  } | null>(null);

  const handleRequestCredits: () => Promise<void> = async () => {
    setIsRequestingCredits(true);
    try {
      const response = await requestCredits();
      setMantineAlert({
        message: response.message,
        color: "green",
      });
      setHasPendingRequest(true);
    } catch (error) {
      console.error("Error requesting credits:", error);

      // Check if this is an AxiosError with a response
      if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError<any>;

        // Check if it's the specific error for already having a pending request
        if (
          axiosError.response?.status === 400 &&
          axiosError.response?.data?.message?.includes(
            "already requested credits"
          )
        ) {
          setMantineAlert({
            message:
              "You already have a pending credit request. Your administrator will review it soon.",
            color: "blue", // Using blue for informational messages rather than red for errors
          });
          setHasPendingRequest(true);
        } else {
          // Handle other errors
          setMantineAlert({
            message: "Failed to request credits. Please try again later.",
            color: "red",
          });
        }
      } else {
        // Handle non-Axios errors
        setMantineAlert({
          message: "Failed to request credits. Please try again later.",
          color: "red",
        });
      }
    } finally {
      setIsRequestingCredits(false);
    }
  };

  return {
    isRequestingCredits,
    mantineAlert,
    handleRequestCredits,
    hasPendingRequest,
  };
};

export default useRequestCredits;
